import { useEffect, useMemo, useRef } from 'react';
import { useMileage } from '../data/useMileage';
import { useExpenses } from '../data/useExpenses';
import useMileageExpenseSettings from './useMileageExpenseSettings';
import { todayISO } from '../domain/clock';
import { planTopUp } from '../domain/mileage';

// What the studio's recurring trips are owed in expenses and have not been
// given (#554), written once the trips and the expenses have both been read.
// `plan_top_up` in core/src/mileage.rs is the rule; its two cases are the
// year's turn — a series' expenses are written through 31 December at each
// save, so an indefinite one's next year is written here — and a series
// saved before #554, whose one recurring expense becomes one a date.
//
// Run from the Mileage Log and the Expenses page, the two places those
// expenses are looked at, rather than from the app's root: it writes, and a
// writer mounted under every route would write behind a paywall the rules
// refuse and inside every test that mounts the providers. Almost always the
// plan is empty and this writes nothing. The ids it has written are kept, so
// a snapshot arriving before the queue's overlay does cannot write them twice
// — a second write would land on the same derived ids anyway.
export default function useMileageTopUp() {
  const { trips, loading: tripsLoading, topUpExpenses } = useMileage();
  const { entries, loading: expensesLoading } = useExpenses();
  const expense = useMileageExpenseSettings();
  const written = useRef('');
  const ids = useMemo(() => entries.map((e) => e.id), [entries]);

  useEffect(() => {
    if (tripsLoading || expensesLoading || !expense.enabled) return;
    const plan = planTopUp(trips, ids, expense, todayISO());
    if (!plan.length) return;
    const key = plan.map((w) => w.id).join('|');
    if (written.current === key) return;
    written.current = key;
    // A refusal reaches SyncContext and `onWriteRefused`, as every write's
    // does; there is nothing for this effect to add. A rejection is also the
    // server not answering (`topUpExpenses` asks it which expenses exist,
    // #631), and the next visit asks again.
    topUpExpenses(ids, expense).then(() => {}, () => {});
  }, [trips, ids, expense, tripsLoading, expensesLoading, topUpExpenses]);
}
